// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface ModalButton {
	key: string;
	label: string;
	value: any;
	selected?: boolean;
}

@customElement('confirm-dialog')
export class ConfirmDialog extends LitElement {
	@property({ type: String })
	message = '';

	@state()
	private open = false;

	static override styles = css`\
:host {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	display: none;
	justify-content: center;
	align-items: center;
	z-index: 1000;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

:host([open]) {
	display: flex;
}

.overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.4);
	display: flex;
	justify-content: center;
	align-items: center;
}

.modal {
	position: relative;
	padding: 40px;
	max-width: 500px;
	width: 90%;
	animation: modalEnter 0.3s ease-out;
}

@keyframes modalEnter {
	from {
		opacity: 0;
		transform: scale(0.9) translateY(-20px);
	}
	to {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
}

.background {
	background-image: linear-gradient(
		90deg,
		rgba(255, 0, 0, 0),
		rgba(34, 34, 34, 0.8) 20%,
		rgba(34, 34, 34, 0.8) 80%,
		rgba(255, 0, 0, 0)
	);
}

.message {
	color: #ffffff;
	font-size: 16px;
	font-weight: 500;
	line-height: 1.5;
	text-align: center;
	margin: 40px;
	opacity: 0.9;
}

.buttons {
	display: flex;
	justify-content: center;
	gap: 30px;
	margin-top: 30px;
}

button {
	background: none;
	border: none;
	display: flex;
	gap: 8px;
	align-items: center;
}

.control-hint {
	color: #fff;
}

.control-key {
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 4px;
	padding: 2px 6px;
	font-family: monospace;
	font-size: 10px;
	color: #ccc;
	text-transform: uppercase;
}

.top-decoration, .bottom-decoration {
	width: 100%;
	height: 20px;
}

@media (max-width: 600px) {
	.modal {
		padding: 30px 20px;
		margin: 20px;
	}

	.buttons {
		flex-direction: column;
		gap: 15px;
	}
}`;

	private _onKeyDown = this.onKeyDown.bind(this);
	private _isKeyDownListening = false;

	private addKeyDownListener(): void {
		if(!this._isKeyDownListening) {
			this._isKeyDownListening = true;
			document.addEventListener('keydown', this._onKeyDown);
		}
	}

	private removeKeyDownListener(): void {
		if(this._isKeyDownListening) {
			this._isKeyDownListening = false;
			document.removeEventListener('keydown', this._onKeyDown);
		}
	}

	private onKeyDown(event: KeyboardEvent) {
		if(!this.open) return;

		switch(event.key) {
		case 'Escape':
			this.cancel();
			break;
		case 'Space':
			this.confirm();
			break;
		}
	};

	override connectedCallback() {
		super.connectedCallback();
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		this.removeKeyDownListener();
	}

	static confirm(dialog: ConfirmDialog, message: string): Promise<boolean> {
		return new Promise((resolve) => {
			function onConfirm() {
				cleanup();
				resolve(true);
			}
			function onCancel() {
				cleanup();
				resolve(false);
			}
			function cleanup() {
				dialog.removeEventListener('confirm', onConfirm);
				dialog.removeEventListener('cancel', onCancel);
			}

			dialog.message = message;
			dialog.addEventListener('confirm', onConfirm);
			dialog.addEventListener('cancel', onCancel);
			dialog.openDialog();
		});
	}

	openDialog() {
		this.open = true;
		this.addKeyDownListener();
	}

	closeDialog() {
		this.open = false;
		this.removeKeyDownListener();
	}

	private confirm() {
		this.closeDialog();
		this.dispatchEvent(new CustomEvent('confirm'));
	}

	private cancel() {
		this.closeDialog();
		this.dispatchEvent(new CustomEvent('cancel'));
	}

	private getBunnyIcon(): any {
		return html`\
<svg class="bunny-icon" xmlns="http://www.w3.org/2000/svg" height="24" width="100%" fill="#e8eaed">
	<defs>
		<pattern id="bunny-tile" patternUnits="userSpaceOnUse" viewBox="0 -960 960 960" width="24" height="24">
			<path d="M380-80q-75 0-127.5-52.5T200-260q0-35 17-64.5t63-75.5q6-6 11.5-12.5T306-430q-51-78-78.5-163.5T200-760q0-58 21-89t59-31q57 0 102 55t68 101q9 20 16.5 40.5T480-641q6-22 13.5-42.5T511-724q22-46 67-101t102-55q38 0 59 31t21 89q0 81-27.5 166.5T654-430q9 11 14.5 17.5T680-400q46 46 63 75.5t17 64.5q0 75-52.5 127.5T580-80q-45 0-72.5-10L480-100l-27.5 10Q425-80 380-80Zm0-80q23 0 46-5.5t43-16.5q-11-5-20-17t-9-21q0-8 11.5-14t28.5-6q17 0 28.5 6t11.5 14q0 9-9 21t-20 17q20 11 43 16.5t46 5.5q42 0 71-29t29-71q0-18-10-35t-30-34q-14-12-23-21t-29-34q-29-35-48-45.5T480-440q-41 0-60.5 10.5T372-384q-20 25-29 34t-23 21q-20 17-30 34t-10 35q0 42 29 71t71 29Zm40-130q-8 0-14-9t-6-21q0-12 6-21t14-9q8 0 14 9t6 21q0 12-6 21t-14 9Zm120 0q-8 0-14-9t-6-21q0-12 6-21t14-9q8 0 14 9t6 21q0 12-6 21t-14 9ZM363-489q11-8 25-14t31-11q-2-48-14.5-95.5T373-696q-19-40-42-67.5T285-799q-2 6-3.5 15.5T280-760q0 68 21.5 138T363-489Zm234 0q40-63 61.5-133T680-760q0-14-1.5-23.5T675-799q-23 8-46 35.5T587-696q-18 39-30.5 86.5T541-514q15 4 29 10.5t27 14.5Z"></path>
		</pattern>
		<linearGradient id="alpha-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
			<stop offset="0%" style="stop-color:white;stop-opacity:0"></stop>
			<stop offset="20%" style="stop-color:white;stop-opacity:0.8"></stop>
			<stop offset="80%" style="stop-color:white;stop-opacity:0.8"></stop>
			<stop offset="100%" style="stop-color:white;stop-opacity:0"></stop>
		</linearGradient>
		<mask id="alpha-mask">
			<rect x="0" y="0" width="100%" height="100%" fill="url(#alpha-gradient)"></rect>
		</mask>
  	</defs>
	<rect x="0" y="0" width="100%" height="100%" fill="url(#bunny-tile)" mask="url(#alpha-mask)"></rect>
</svg>`;
	}

	override render(): TemplateResult {
		return html`\
<div class="overlay">
	<div class="modal">
		<div class="background">
			<div class="top-decoration">${this.getBunnyIcon()}</div>
			<div class="message">${this.message}</div>
			<div class="bottom-decoration">${this.getBunnyIcon()}</div>
		</div>
		<div class="buttons">
			<button @click=${() => this.confirm()}>
				<span class="control-key">Space</span>
				<span class="control-hint">Confirm</span>
			</button>
			<button @click=${() => this.cancel()}>
				<span class="control-key">Esc</span>
				<span class="control-hint">Cancel</span>
			</button>
		</div>
	</div>
</div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'confirm-dialog': ConfirmDialog;
	}
}
